import { createElement, type ReactElement, type ReactNode } from 'react';

/**
 * The anatomy of a panel section (D548). Put them inside `<Panel padded={false}>` (the flush panel): a head with the title and
 * the section's actions under a hairline, then a body with the standard padding — or a `ListTable`/`KeyValueList` flush with the
 * edges. Replaces the `Panel style={{ padding: 0 }}` + hand-styled heading recipes (44 + 106 inline styles in the Admin).
 */

export interface PanelHeadProps {
  /** The section title (a heading). */
  readonly title: ReactNode;
  /** Heading level. Default 2. */
  readonly level?: 2 | 3 | 4;
  /** Controls on the right (an icon button, a link, a segmented filter). */
  readonly actions?: ReactNode;
  /** The compact head for an icon button on the right (8px at the edges instead of 16). */
  readonly dense?: boolean;
  /** Pair with `aria-labelledby` of a region. */
  readonly titleId?: string;
}

/** A panel's head: title left (14px / 700), actions right, hairline underneath. */
export function PanelHead({ title, level = 2, actions, dense = false, titleId }: PanelHeadProps): ReactElement {
  return (
    <header className="vello-panelhead" data-dense={dense ? 'true' : undefined}>
      {createElement(`h${level}`, { id: titleId, className: 'vello-panelhead__title' }, title)}
      {actions !== undefined && <div className="vello-panelhead__actions">{actions}</div>}
    </header>
  );
}

/** A panel's padded body (use `flush` when the content brings its own edges, e.g. a table). */
export function PanelBody({ children, flush = false }: { children: ReactNode; flush?: boolean }): ReactElement {
  return (
    <div className="vello-panelbody" data-pad={flush ? 'none' : undefined}>
      {children}
    </div>
  );
}
